/**
 * Estimate before building (W4 §4): at the outline/storyboard review stop, where the user says go,
 * what the video is likely to use against what the wallet has. An estimate, never a cap.
 */

import { useEffect, useState } from 'react';
import { billingAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { studioBOn } from '../../lib/studioB/flag';
import { getVideoEstimate, type VideoEstimate, type VideoSku } from './videoEstimate';

const credits = (milli: number) => Math.round(milli / 1000);

export function BuildEstimate({ sku, tier }: { sku: VideoSku; tier?: string }) {
  const [estimate, setEstimate] = useState<VideoEstimate | null>(null);
  const [availableMilli, setAvailableMilli] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    getVideoEstimate({ sku, tier }).then((e) => { if (!cancelled) setEstimate(e); }).catch(() => {});
    billingAPI.getBalance().then((b) => { if (!cancelled) setAvailableMilli(b.availableMilli); }).catch(() => {});
    return () => { cancelled = true; };
  }, [sku, tier]);

  if (!estimate || availableMilli === null) return null;

  const low = credits(estimate.lowMilli);
  const high = credits(estimate.highMilli);
  const short = availableMilli < estimate.highMilli;
  const words = `This video will use about ${low === high ? low : `${low}–${high}`} credits. You have ${Number((Math.max(0, availableMilli) / 1000).toFixed(2))}.`;

  if (studioBOn()) {
    return (
      <p data-estimate className={cn('flex items-center gap-2 px-1 text-b-meta', short ? 'text-b-t2' : 'text-b-t3')}>
        {short && <span data-mark aria-hidden className="h-2 w-2 flex-none rounded-full bg-amber-500" />}
        {words}
      </p>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2 text-xs',
        short ? 'border-amber-500/40 bg-amber-500/10' : 'border-border-secondary studio-text-secondary',
      )}
    >
      This video will use about {low === high ? low : `${low}–${high}`} credits. You have {Number((Math.max(0, availableMilli) / 1000).toFixed(2))}.
    </div>
  );
}
